import { useParams, useNavigate, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Loader from './components/Loader';
import Slider from 'react-slick';
import { eventsRepository } from '../../repositories/EventsRepository';
import { Event, FormatDate, getDistanceBetweenCoordinateAndEvent } from '../../models/Event';
import UserBadge from './components/UserBadge';
import { useConfirm } from '../../hooks/useConfirm';
import { useRouter } from '../../hooks/useRouter';
import { ConnectionButton } from './components/ConnectionButton';
import { plebsRepository } from '../../repositories/PlebsRepository';
import { canManageMembers, Pleb } from '../../models/Pleb';
import Clickable from '../../components/Clickable';
import { ChatChannelType } from '../../models/ChatChannel';
import { LayoutWithTopBar } from '../../components/LayoutWithTopBar';
import { Button, ButtonSize } from '../../components/Button';
import { ButtonVariant } from '../../config/colors';
import { CopyToClipboard } from '../../components/CopyToClipboard';
import { decode } from 'he';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ProfilePageEventProps {}

export const ProfilePageEvent: React.FC<ProfilePageEventProps> = () => {
  const { eventId } = useParams();
  const {pleb} = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [distance, setDistance] = useState<string | null>(null);
  const [organizer, setOrganizer] = useState<Pleb | null>(null);
  const [eventLink, setEventLink] = useState<string | null>(null);
  const navigate = useNavigate();
  const {navigateFromFullChannelId, navigateToProfile, navigateToEventParticipants} = useRouter();
  const {openConnectDialog} = useConfirm();
  const location = useLocation();
  const {openConfirm} = useConfirm();

  const handleClickConnect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if(event?.organizer) {
      openConnectDialog(event?.organizer?.toJson() ?? {});
    }
  }

  const isCurrentUserOrganizer = event?.organizer?.id === pleb?.id;

  const fetchEventDetails = async () => {
    try {
      const eventData = await eventsRepository.getEventDetails(eventId ?? '', pleb?.id ?? '');
      setEvent(eventData);
      if (eventData?.organizer) {
        delete eventData.organizer.connection;
        setOrganizer(eventData.organizer);
        const organizer = await plebsRepository.getUserDetails(eventData.organizer.id, pleb?.id ?? '');
        setOrganizer(organizer);
      } else {
        setOrganizer(null);
      }
      if (eventData && pleb?.coordinates) {
        setDistance(getDistanceBetweenCoordinateAndEvent(pleb.coordinates, eventData));
      } else {
        setDistance(null);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  };

  const deleteEvent = (id: string) => {
    openConfirm({
      message: 'Are you sure you want to delete this event?',
      buttons: [
          {
            label: 'Delete', 
            onClick: () => {
              eventsRepository.deleteEvent(id)
                .then(() => {
                  setEvent(null);
                })
            }, 
            variant: ButtonVariant.danger
          },
          {
            label: 'Cancel', 
            variant: ButtonVariant.transparent
          },
      ]
    });
  }

  useEffect(() => {
    setEvent(null);
    fetchEventDetails();
  }, [eventId, pleb?.id, pleb]);

  useEffect(() => {
    if(event) {
      eventsRepository.getEventShareLink(event.id)
        .then((link) => {
          setEventLink(link);
        });
    }
  }, [event]);

  const sliderSettings = {
    dots: true,
    infinite: false
  };

  const gmapLink = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent((event?.venue ? event.venue + ', ' : '') + (event?.address ?? ''))

  const cancelRsvp = () => {
    openConfirm({
      title: 'Cancel RSVP?',
      message: 'You are about to cancel your RSVP for this event. Are you sure ?',
      buttons: [
        { label: 'Cancel RSVP', variant: ButtonVariant.red2, onClick: () => {
          eventsRepository.unrsvpEvent(event?.id ?? '', pleb?.id ?? '')
            .then((event) => {
              setEvent(event);
            })
            .catch((error) => {
              console.error('Error unRSVPing to event:', error);
            });
        } },
        { label: 'Go back', variant: ButtonVariant.inverted, onClick: () => {} },
      ]
    })
  }

  return (
    <LayoutWithTopBar
      onClickPrevArrow={() => navigate(-1)}
      topBarChildren={
        <div className="flex items-center">
          <h1 className="text-l font-bold text-center">{event?.name}</h1>
        </div>
      }
    >
      {
        event ? (
          <div className="max-w-[440px] w-full mx-auto py-6">
            <div className="rounded-lg bg-brand-bg-5 overflow-hidden" style={{ maxWidth: '100vw' }}>
              <div className="relative">
                <div className="relative">
                  <Slider {...sliderSettings}>
                    {
                      event.pictures.map((picture, index) => (
                        <div key={index}>
                          <img src={picture} alt={event.name} className="w-full aspect-square object-cover" />
                        </div>
                      ))
                    }
                  </Slider>
                  <div className="absolute bottom-4 left-4">
                  <Button 
                    variant={ButtonVariant.primary} 
                    size={ButtonSize.small} 
                    className="gap-1 rounded-xl px-1"
                  >
                    <CopyToClipboard toCopy={eventLink ?? ''} copiedText="Copied">
                      <div className="flex items-center gap-2 px-2">
                        <span className="material-symbols-outlined text-[14px]">ios_share</span>
                        <span>Share</span>
                      </div>
                    </CopyToClipboard>
                  </Button>
                </div>
                {/* RSVP Button Absolute Bottom-Right */}
                {event.rsvpCount > 1 && (
                  <div className="absolute bottom-4 right-4">
                    <Button
                      variant={ButtonVariant.primary}
                      size={ButtonSize.small}
                      className="flex items-center gap-2 rounded-xl px-2 py-1"
                      onClick={() => {
                        navigateToEventParticipants(event.id);
                      }}
                      style={{ fontWeight: 700, fontSize: 11 }}
                    >
                      <div className="flex -space-x-2">
                        {event.rsvpPreview?.map((preview, idx) => (
                          <img
                            key={preview.id}
                            src={preview.thumbnail || preview.image}
                            alt={preview.name}
                            className="w-6 h-6 rounded-full z-[${3-idx}]"
                            style={{ zIndex: 3 - idx }}
                          />
                        ))}
                      </div>
                      <span className="text-white">{event.rsvpCount} GOING</span>
                    </Button>
                  </div>
                )}
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between items-stretch mb-4 gap-2">
                  {
                    isCurrentUserOrganizer ? (
                      <Button
                          variant={ButtonVariant.inverted}
                          size={ButtonSize.medium}
                          className="rounded-xl px-2 py-1 flex-1"
                          onClick={() => {
                            navigate(`/home/event/edit-event/${event.id}`);
                          }}
                        >
                          EDIT EVENT
                        </Button>
                    ) : (
                      event.isRsvp ? (
                        <>
                          <Button
                            variant={ButtonVariant.green2}
                            size={ButtonSize.medium}
                            className="rounded-xl px-2 py-1 flex-1"
                            onClick={() => {
                              cancelRsvp();
                            }}
                          >
                            YOU ARE GOING!
                          </Button>
                          <Button
                            variant={ButtonVariant.red2}
                            size={ButtonSize.medium}
                            className="rounded-xl px-2 py-1"
                            onClick={() => {
                              cancelRsvp();
                            }}
                          >
                            <span className="material-symbols-outlined">close</span>
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant={ButtonVariant.inverted}
                          size={ButtonSize.medium}
                          className="rounded-xl px-2 py-1 flex-1"
                          onClick={() => {
                            openConfirm({
                              title: 'RSVP to this event?',
                              message: 'You will be added to the event group chat and reminded before it starts. Are you sure you want to confirm?',
                              buttons: [
                                { label: 'Yes, RSVP', variant: ButtonVariant.green2, onClick: () => {
                                  eventsRepository.rsvpEvent(event.id, pleb?.id ?? '')
                                    .then((event) => {
                                      setEvent(event);
                                    })
                                    .catch((error) => {
                                      console.error('Error RSVPing to event:', error);
                                    });
                                } },
                                { label: 'Go back', variant: ButtonVariant.inverted, onClick: () => {} },
                              ]
                            })
                            
                          }}
                        >
                          RSVP
                        </Button>
                      )
                    )
                  }
                </div>

                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold">{event.name}</h2>
                  </div>                    
                </div>

                <hr className="my-3" />

                <div className="space-y-4">
                  {
                    distance && (
                      <div className="mb-4 italic">
                        {distance} away
                      </div>
                    )
                  }

                  {event.startDate && (
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-[50px] h-[50px] shrink-0">
                        <i className="text-[42px] material-symbols-outlined">calendar_today</i>
                      </div>
                      <div className="ml-2 flex flex-col">
                        <span className="text-brand-orange">{event.getDateFormatted(FormatDate.date)}</span>
                        <span>{event.getDateFormatted(FormatDate.hour)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-[50px] h-[50px] shrink-0">
                      <i className="text-[42px] material-symbols-outlined">location_on</i>
                    </div>
                    <div className="ml-2 flex flex-col">
                      <a href={gmapLink} target='_blank' rel="noreferrer" className="text-brand-orange">{event.venue}</a>
                      <a className="underline" href={gmapLink} target='_blank' rel="noreferrer">{event.address}</a>
                    </div>
                  </div>

                  {
                    organizer ? (
                      <Clickable 
                        className="flex items-center rounded-full"
                        onClick={() => navigateToProfile(ChatChannelType.Messaging, organizer.id, location.pathname)}
                      >
                        <img
                          src={organizer.image}
                          alt="Avatar"
                          className="w-[50px] h-[50px] object-cover rounded-full overflow-hidden bg-white"
                        />
                        
                        <div className="ml-2 mr-2 flex-1 break-words [word-break:break-word]">
                          <span className="text-brand-orange">Organizer</span>
                          <div className="flex items-center">
                            <span className="font-semibold break-words [word-break:break-word]">
                              {organizer.name}
                            </span>
                            
                            {organizer.badge && (
                              <div className="mx-1">
                                <UserBadge badge={organizer.badge} />
                              </div>
                            )}
                          </div>
                        </div>

                        {
                          organizer.connection &&
                            <ConnectionButton   
                              connection={organizer.connection}
                              onConnect={handleClickConnect}
                              onChat={(connectionId) => {
                                navigateFromFullChannelId(connectionId, location.pathname);
                              }}
                              allowPressAwaitingReply={(pleb && canManageMembers(pleb)) ? true : false}
                            />
                        }
                      </Clickable>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openConfirm({
                          title: 'Are you the organizer of this event?',
                          buttons: [
                            { label: 'Claim Event', variant: ButtonVariant.primary, onClick: () => {
                              eventsRepository.claimEvent(event?.id ?? '', pleb?.id ?? '')
                                .then(() => fetchEventDetails())
                                .catch((err) => console.error('Error claiming event:', err));
                            } },
                            { label: 'Cancel', variant: ButtonVariant.inverted, onClick: () => {} },
                          ],
                        })}
                        className="flex items-center w-full text-left bg-transparent border-0 p-0 cursor-pointer hover:bg-brand-bg-6 rounded-full transition-colors"
                      >
                        <div className="flex items-center justify-center w-[50px] h-[50px] shrink-0">
                          <i className="text-[42px] material-symbols-outlined">add</i>
                        </div>
                        <div className="ml-2 flex flex-col">
                          <span className="text-brand-orange">Claim event</span>
                        </div>
                      </button>
                    )
                  }

                  {event.description && (
                    <div className="prose max-w-none">
                      <div className="flex items-center justify-between gap-2 mt-6 mb-2">
                        <h2 className="text-xl font-bold">About this event</h2>
                          <Button
                            variant={ButtonVariant.inverted}
                            onClick={() => {
                              eventsRepository.joinEventGroupChat(event.id, pleb?.id ?? '')
                                .then(() => {
                                  navigate(`/home/chat/chat/event/${event.id}`);
                                })
                                .catch((err) => console.error('Error joining event group chat:', err));
                            }}
                            size={ButtonSize.small}
                          >
                            <span className="material-symbols-outlined text-[14px]">chat_bubble</span>
                            Group chat
                          </Button>
                      </div>
                      <div className="prose max-w-none whitespace-pre-line container-underline-links">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
                          }}
                        >
                          {decode(event.description)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

        <div className="px-6 flex justify-center">
                {
                  pleb && canManageMembers(pleb) && (
                    <Button 
                      variant={ButtonVariant.transparent} 
                      size={ButtonSize.small} 
                      onClick={() => deleteEvent(event.id)}
                      className="underline"
                    >
                      Delete event
                    </Button>
                  )
                }
                </div>

                </div>
              </div>
            </div>
          </div>
        ) : (
          <Loader />
        )
      }
    </LayoutWithTopBar>
  );
};

export default ProfilePageEvent;